import * as Sentry from "@sentry/tanstackstart-react";
import { isNotFound, isRedirect } from "@tanstack/react-router";
import { createMiddleware } from "@tanstack/react-start";
import { resolveClientIp } from "#/modules/util.instrumentation/client-ip.server";

/**
 * One GlitchTip log line for every request that ends in a 4xx/5xx or a throw, whichever layer
 * produced it: a request middleware that answers before the handler runs (CSRF), a server route,
 * a server function or the SSR document. Must sit directly after the Sentry middleware so every
 * rejection further down the chain passes through it. The fetch-metadata headers are included
 * because they decide the CSRF outcome.
 */
export const requestLogMiddleware = createMiddleware().server(
  async ({ next, request, pathname, handlerType }) => {
    const startedAt = performance.now();
    const attributes = () => ({
      "http.method": request.method,
      "url.path": pathname,
      "handler.type": handlerType,
      "http.sec_fetch_site": request.headers.get("sec-fetch-site") ?? "",
      "http.origin": request.headers.get("origin") ?? "",
      "http.referer": request.headers.get("referer") ?? "",
      "user_agent.original": request.headers.get("user-agent") ?? "",
      "client.address": resolveClientIp(request) ?? "",
      duration_ms: Math.round(performance.now() - startedAt),
    });

    try {
      const result = await next();
      const status = result.response.status;
      if (status >= 400) {
        const log = status === 404 ? Sentry.logger.info : Sentry.logger.warn;
        log(`request failed ${status}`, { ...attributes(), "http.status_code": status });
      }
      return result;
    } catch (cause) {
      const isControlFlow = cause instanceof Response || isRedirect(cause) || isNotFound(cause);
      if (!isControlFlow) {
        Sentry.logger.warn("request threw", {
          ...attributes(),
          "error.message": cause instanceof Error ? cause.message : String(cause),
        });
      }
      throw cause;
    }
  },
);
